<template>
  <div
    class="w-full text-white bg-[#0F172B] bg-[radial-gradient(circle_at_top,#1C2A52_0%,#0F172B_45%,#0B1222_100%)]"
  >
    <div class="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Top pills -->
      <div class="flex justify-center">
        <div
          class="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1 backdrop-blur-sm"
        >
          <button
            v-for="group in groupsForNav"
            :key="group._key"
            type="button"
            class="px-5 py-2 text-sm font-semibold rounded-full transition-colors"
            :class="
              activeGroupKey === group._key
                ? 'bg-linear-to-r from-[#6E28FD] to-purple-500 text-white'
                : 'text-white/75 hover:text-white hover:bg-white/5'
            "
            @click="scrollToGroup(group)"
          >
            {{ group._displayTitle }}
          </button>
        </div>
      </div>

      <!-- Groups -->
      <section
        v-for="group in groupsToRender"
        :key="group._key"
        :id="group._anchorId"
        class="pt-14"
      >
        <h2
          class="text-4xl sm:text-5xl font-extrabold tracking-tight text-center"
        >
          {{ group._displayTitle }}
        </h2>
        <p class="mt-4 text-center text-white/60 max-w-3xl mx-auto">
          {{ group._displayDescription }}
        </p>

        <div
          class="mt-10 grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(260px,260px))] gap-5 justify-center"
        >
          <NuxtLink
            v-for="model in group._models"
            :key="model.id ?? model.url"
            :to="modelLink(model)"
            class="group block rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
          >
            <div class="p-5">
              <div class="flex items-center gap-3">
                <div
                  class="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden"
                >
                  <img
                    v-if="model?.avatar?.src"
                    :src="model.avatar.src"
                    :alt="modelTitle(model)"
                    class="h-7 w-7 object-contain"
                    loading="lazy"
                  />
                </div>
                <div class="min-w-0">
                  <div class="font-semibold text-white truncate">
                    {{ modelTitle(model) }}
                  </div>
                  <div v-if="modelVendor(model)" class="mt-0.5 text-xs text-white/45 truncate">
                    {{ modelVendor(model) }}
                  </div>
                </div>
              </div>

              <p class="mt-4 text-sm text-white/60 line-clamp-3">
                {{ modelDescription(model) }}
              </p>
            </div>
          </NuxtLink>

          <div
            v-if="!group._models.length"
            class="rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-white/60"
          >
            Coming soon.
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { modelGroups, toInternalRoute } from "@/utils/aiCatalogData";

definePageMeta({ layout: "sign-new" });

const title =
  "AI Models – Explore Top AI Video, Image & Music Generators | SharkFoto";
const description =
  "Discover Top & Hot AI Models – cutting-edge video, image & music generators. Compare models, explore features & find your perfect AI tool for creative content.";
const cdnUrl = useRuntimeConfig().public.cdnUrl;
useSeoMeta({
  title,
  description,
  canonical: "https://sharkfoto.com/ai/models/",
  ogTitle: title,
  ogDescription: description,
  ogImage: `${cdnUrl}/sharkfoto_og.png`,
  ogUrl: "https://sharkfoto.com/ai/models/",
  ogSiteName: "SharkFoto",
  ogType: "website",
  ogLocale: "en_US",
  twitterCard: "summary_large_image",
  twitterUrl: "https://sharkfoto.com/ai/models/",
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: `${cdnUrl}/sharkfoto_og.png`,
  twitterSite: "@Shark_Foto",
  twitterCreator: "@Shark_Foto",
});

const modelList = inject("modelList", ref([]));
const aiModelGroups = computed(() => modelGroups(modelList.value));

const anchorIdFromTitle = (title) =>
  `group-${String(title || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}`;

const modelLink = (model) => {
  const url = model?.url || "";
  if (typeof url === "string" && url.startsWith("http")) return url;
  return toInternalRoute(url);
};

const modelTitle = (model) => model?.modelName || model?.title || "AI Model";

const modelDescription = (model) => model?.description || "";

const modelVendor = (model) => model?.vendor || "";

const groupsToRender = computed(() =>
  (aiModelGroups.value || []).map((group) => ({
    ...group,
    _key: String(group.id ?? group.title),
    _anchorId: anchorIdFromTitle(group.title),
    _displayTitle: group.title,
    _displayDescription: group.description || "",
    _models: group.models || [],
  }))
);

const groupsForNav = computed(() =>
  (groupsToRender.value || []).filter((g) => g._models?.length > 0)
);


const activeGroupKey = ref(groupsForNav.value?.[0]?._key || "");

const getStickyHeaderOffset = () => {
  const stickyHeader = document.querySelector(".sticky.top-0");
  if (!stickyHeader) return 0;
  return Math.ceil(stickyHeader.getBoundingClientRect().height);
};

const getScrollContainer = (el) => {
  if (!el) return null;
  // signNew.vue 的右侧滚动容器有 right-scroll class
  return el.closest(".right-scroll") || document.querySelector(".right-scroll");
};

const scrollToGroup = (group) => {
  if (!group?._anchorId) return;
  activeGroupKey.value = group._key;
  const el = document.getElementById(group._anchorId);
  if (!el) return;

  // 解决 sticky 顶部导航遮挡：滚动时预留 header 高度
  const offset = getStickyHeaderOffset() + 12; // 额外留一点间距
  const container = getScrollContainer(el);

  if (container) {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - containerRect.top + container.scrollTop - offset;
    container.scrollTo({ top, behavior: "smooth" });
    return;
  }

  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
};

watch(
  groupsForNav,
  (next) => {
    if (!activeGroupKey.value && next?.length) {
      activeGroupKey.value = next[0]._key;
    }
  },
  { immediate: true }
);
</script>
